
/* === sticky-columns.js === */
if (!customElements.get('sticky-columns')) {
  customElements.define('sticky-columns', class StickyColumns extends HTMLElement {
    constructor() {
      super();
      this.columns = [];
      this.tallestColumn = null;
      this.isEnabled = false;
      this.resizeTimeout = null;
      this.resizeObserver = null;
      this.mediaQuery = null;
      this.boundMediaChange = null;
    }

    connectedCallback() {
      this.updateOffset();
      this.collectColumns();

      if (this.columns.length < 2) return;

      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.boundMediaChange = (e) => this.handleMediaChange(e);
      this.mediaQuery.addEventListener('change', this.boundMediaChange);

      if (this.mediaQuery.matches) {
        setTimeout(() => this.enable(), 100);
      }

      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.recalculate(), 150);
      });
      this.columns.forEach(col => this.resizeObserver.observe(col.element));

      this.observeStickyOffset();
    }

    disconnectedCallback() {
      this.disable();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.mediaQuery && this.boundMediaChange) {
        this.mediaQuery.removeEventListener('change', this.boundMediaChange);
      }
      clearTimeout(this.resizeTimeout);
    }

    updateOffset() {
      const stickyHeight = parseInt(getComputedStyle(document.documentElement)
        .getPropertyValue('--sticky-total-height')) || 0;
      const extraOffset = parseInt(this.dataset.stickyOffset) || 20;
      this.topOffset = stickyHeight + extraOffset;
    }

    collectColumns() {
      const columnElements = this.querySelectorAll(':scope > .flexible-columns__column');

      columnElements.forEach((el, index) => {
        const hasContent = el.children.length > 0 || el.textContent.trim().length > 0;
        if (!hasContent) return;

        this.columns.push({
          element: el,
          index: index,
          height: 0,
          isSticky: false
        });
      });
    }

    calculateHeights() {
      let maxHeight = 0;
      let tallestIndex = 0;

      this.columns.forEach((col) => {
        col.element.style.position = '';
        col.element.style.top = '';
        col.element.style.alignSelf = 'flex-start';
        col.element.classList.remove('flexible-columns__column--sticky');
      });

      this.offsetHeight;

      this.columns.forEach((col, i) => {
        col.height = col.element.scrollHeight;

        if (col.height > maxHeight) {
          maxHeight = col.height;
          tallestIndex = i;
        }
      });

      this.tallestColumn = this.columns[tallestIndex];

      const threshold = 50;
      this.columns.forEach((col) => {
        const diff = this.tallestColumn.height - col.height;
        col.isSticky = col !== this.tallestColumn && diff > threshold;
      });
    }

    enable() {
      this.updateOffset();
      this.calculateHeights();
      this.applyStyles();
      this.isEnabled = true;
    }

    disable() {
      this.isEnabled = false;
      this.resetStyles();
    }

    applyStyles() {
      this.columns.forEach(col => {
        if (col.isSticky) {
          col.element.style.position = 'sticky';
          col.element.style.top = `${this.topOffset}px`;
          col.element.style.alignSelf = 'flex-start';
          col.element.classList.add('flexible-columns__column--sticky');
        }
      });
    }

    resetStyles() {
      this.columns.forEach(col => {
        col.element.style.position = '';
        col.element.style.top = '';
        col.element.style.alignSelf = '';
        col.element.classList.remove('flexible-columns__column--sticky');
      });
    }

    recalculate() {
      if (!this.mediaQuery.matches) return;

      this.resetStyles();

      requestAnimationFrame(() => {
        this.updateOffset();
        this.calculateHeights();
        this.applyStyles();
      });
    }

    handleMediaChange(e) {
      if (e.matches) {
        this.enable();
      } else {
        this.disable();
      }
    }

    observeStickyOffset() {
      const observer = new MutationObserver(() => {
        const oldOffset = this.topOffset;
        this.updateOffset();

        if (oldOffset !== this.topOffset && this.isEnabled) {
          this.applyStyles();
        }
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style']
      });
    }
  });
}

